import { PlatformModule } from '@angular/cdk/platform';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  InMemoryScrollingOptions,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { createAuthInterceptor, provideRmaTokenProvider } from '@rma/authentication/public/api-authentication';
import { provideRmaUserInitialisation } from '@rma/authentication/public/user';
import { PaymentMethodAlertServiceModule } from '@rma/billing/public/orc-payment-method-alert';
import { provideRmaBillingTokenProvider } from '@rma/billing/public/util-proxy';
import { provideRmaFeatureFlagsBrowser } from '@rma/common-ui-ng/feature-flags';
import { provideCustomImageLoader } from '@rma/common-ui-ng/images';
import { provideRmaInstance } from '@rma/common-ui-ng/instance';
import {
  appendToRmaApiUrl,
  appendToRmaBillingApiUrl,
  appendToRmaMarketorApiUrl,
  appendToRmaReviewsApiUrl,
  appendToRmaSocialApiUrl,
  appendToRmaTrixelsApiUrl,
  appendToRmaWidgetsApiUrl,
  provideRmaApiUrl,
  provideRmaBaseUrl,
  provideRmaBillingApiUrl,
  provideRmaCdnUrl,
  provideRmaFindMyApiUrl,
  provideRmaIdentityUrl,
  provideRmaImagesUrl,
  provideRmaMarketorApiUrl,
  provideRmaReviewsApiUrl,
  provideRmaSocialApiUrl,
  provideRmaStaticImageUrl,
  provideRmaStaticUrl,
  provideRmaTrixelsApiUrl,
  provideRmaWidgetsApiUrl,
  provideSocialStudioUrl,
} from '@rma/common-ui-ng/util';
import { StripeModule } from '@rma/generic/orc/stripe';
import { RmaModalModule } from '@rma/generic/ui/modal';
import { providePromptRefresh } from '@rma/generic/ui/prompt-refresh';
import { provideRmaEnvironment, rmaProperties } from '@rma/generic/util/environment';
import { provideRmaErrorHandler } from '@rma/generic/util/error-reporting';
import { FeatureFlagUserIdentificationService } from '@rma/generic/util/feature-flags-user';
import { RmaMaterialModule } from '@rma/generic/util/material';
import { provideRmaMetaPixelRouteTracking } from '@rma/generic/util/meta-pixel';
import { provideRmaBrandSuffixTitle } from '@rma/generic/util/page-meta';
import { traceInterceptor } from '@rma/generic/util/traceparent-header-interceptor';
import { localeProviders, provideRmaTranslation } from '@rma/generic/util/translation';
import { Angulartics2RouterlessModule } from 'angulartics2';

import { RmaPreloadingStrategy } from './rma-preload.strategy';
import { appRoutes } from './routes';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const rmaProviders = [RmaMaterialModule.forRoot()];

const moduleProviders = [
  Angulartics2RouterlessModule.forRoot(),

  PlatformModule,
  PaymentMethodAlertServiceModule,
  StripeModule.forRoot(rmaProperties.stripePublishKey),
];

// These are providers we need a the the global level because they are used in services.
// This bloat our bundle size and should be removed when we make our own dialog service
const hackProviders = [RmaModalModule];

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(...rmaProviders, ...moduleProviders, ...hackProviders),
    provideRmaTokenProvider(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        traceInterceptor,
        createAuthInterceptor([
          appendToRmaApiUrl,
          appendToRmaBillingApiUrl,
          appendToRmaMarketorApiUrl,
          appendToRmaReviewsApiUrl,
          appendToRmaSocialApiUrl,
          appendToRmaTrixelsApiUrl,
          appendToRmaWidgetsApiUrl,
        ]),
      ]),
    ),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling(scrollConfig),
      withPreloading(RmaPreloadingStrategy),
    ),
    provideRmaTranslation(),
    provideRmaErrorHandler(),
    providePromptRefresh(),
    provideAnimations(),
    provideRmaMetaPixelRouteTracking(rmaProperties.facebookPixelId),
    provideRmaEnvironment({ ...rmaProperties }),
    provideRmaBrandSuffixTitle(),

    provideRmaFeatureFlagsBrowser(rmaProperties.launchDarklyClientId, FeatureFlagUserIdentificationService),

    provideRmaUserInitialisation(),
    ...localeProviders,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // setting up urls
    provideRmaInstance(rmaProperties.tenant, rmaProperties.environment),
    provideCustomImageLoader(),
    provideRmaApiUrl(),
    provideRmaBaseUrl(),
    provideRmaBillingApiUrl(),
    provideRmaBillingTokenProvider(),
    provideRmaCdnUrl(),
    provideRmaFindMyApiUrl(),
    provideRmaIdentityUrl(),
    provideRmaImagesUrl(),
    provideRmaMarketorApiUrl(),
    provideRmaReviewsApiUrl(),
    provideRmaSocialApiUrl(),
    provideRmaStaticImageUrl(),
    provideRmaStaticUrl(),
    provideRmaTrixelsApiUrl(),
    provideRmaWidgetsApiUrl(),
    provideSocialStudioUrl(),
  ],
};
